<template>
  <v-sheet>
    <v-app-bar
      app
      :color="isDark ? 'theme--dark' : 'white'"
      elevate-on-scroll
      clipped-left
    >
      <v-app-bar-nav-icon
        :title="isDrawerShown ? 'Collapse menu' : 'Expand menu'"
        :aria-label="isDrawerShown ? 'Collapse menu' : 'Expand menu'"
        @click="toggleDrawer(!isDrawerShown)"
      >
        <v-icon>{{
          isDrawerShown ? "mdi-backburger" : "mdi-forwardburger"
        }}</v-icon>
      </v-app-bar-nav-icon>

      <div class="d-inline-flex flex-column justify-center ml-1 mr-4">
        <router-link
          :to="defaultPage"
          class="d-inline-flex align-center"
          aria-label="StorageDefender home"
        >
          <img
            src="/assets/SDLOGO_40.png"
            alt="StorageDefender"
            srcset="/assets/SDLOGO_sm.png 42w, /assets/SDLOGO_40.png 306w"
            sizes="(max-width: 600px) 42px, 306px"
            height="40"
          />
        </router-link>
        <span v-if="buildVersion" class="text-caption grey--text">
          {{ buildVersion }}
        </span>
      </div>

      <v-spacer />
      <v-toolbar-title
        v-if="organizationName"
        class="d-none d-print-block headline"
      >
        {{ organizationName }}
      </v-toolbar-title>
      <FacilityName />
      <PmsSyncNow />

      <v-btn
        icon
        class="action-btn"
        :title="isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
        @click="toggleDarkMode"
      >
        <v-icon size="22" :color="toolbarIconColor">{{
          isDark ? "mdi-weather-sunny" : "mdi-weather-night"
        }}</v-icon>
      </v-btn>
      <TheNotificationMenu class="action-btn" />
      <v-menu
        bottom
        right
        offset-y
        rounded="lg"
        nudge-bottom="8"
        min-width="264"
        transition="slide-y-transition"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            text
            rounded
            height="44"
            min-width="0"
            v-bind="attrs"
            class="mr-3 px-1"
            aria-label="Account menu"
            v-on="on"
          >
            <v-avatar size="32" color="primary">
              <span class="white--text caption font-weight-medium">
                {{ userInitials }}
              </span>
            </v-avatar>
            <v-icon small class="ml-1 grey--text text--darken-1">
              mdi-menu-down
            </v-icon>
          </v-btn>
        </template>

        <v-card width="264" rounded="lg">
          <v-list-item v-if="currentUser" class="pt-3 pb-1">
            <v-list-item-avatar color="primary" size="44">
              <span class="white--text subtitle-1 font-weight-medium">
                {{ userInitials }}
              </span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="subtitle-2 font-weight-bold">
                {{ userName }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                {{ currentUser.username }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-divider class="mx-2" />

          <v-list nav dense class="px-2 py-1">
            <v-list-item to="/account">
              <v-list-item-icon class="mr-3">
                <v-icon>mdi-account-circle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>My Account</v-list-item-title>
            </v-list-item>
            <v-list-item to="/help">
              <v-list-item-icon class="mr-3">
                <v-icon>mdi-help-circle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Get Help</v-list-item-title>
            </v-list-item>
          </v-list>

          <v-divider class="mx-2" />

          <v-list nav dense class="px-2 py-1">
            <v-list-item class="red--text text--darken-1" @click="doLogout">
              <v-list-item-icon class="mr-3">
                <v-icon color="red darken-1">mdi-logout-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Log Out</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>
  </v-sheet>
</template>

<script>
import TheNotificationMenu from "../notifications/TheNotificationMenu.vue";
import PmsSyncNow from "./PmsSyncNow.vue";
import FacilityName from "./FacilityName.vue";

import { computed, unref } from "vue";
import useUser from "../authentication/useUser";
import useNavigation from "./useNavigation";
import useFacility from "../authentication/useFacility";
import useAccessibleScope from "../authentication/useAccessibleScope";
import useDarkMode from "../../composables/useDarkMode";

export default {
  components: {
    TheNotificationMenu,
    PmsSyncNow,
    FacilityName,
  },
  setup() {
    const { currentFacility } = useFacility();
    const { defaultOrganization, defaultPage, logout, currentUser } = useUser();
    const { reachesMultipleFacilities } = useAccessibleScope();
    const { isDrawerShown, toggleDrawer } = useNavigation();
    const { isDark, toggleDarkMode, toolbarIconColor } = useDarkMode();

    const organizationName = computed(
      () => unref(currentFacility)?.name || unref(defaultOrganization)?.name
    );

    const userName = computed(() => {
      const u = currentUser.value;
      if (!u) return "";
      return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
    });
    const userInitials = computed(() => {
      const u = currentUser.value;
      const from = `${u?.firstName?.[0] || ""}${
        u?.lastName?.[0] || ""
      }`.toUpperCase();
      return from || u?.username?.[0]?.toUpperCase() || "?";
    });

    const buildVersion =
      import.meta.env.VUE_APP_BUILD_ENV !== "production"
        ? import.meta.env.VUE_APP_SENTRY_RELEASE
        : null;

    return {
      defaultPage,
      logout,
      isDrawerShown,
      toggleDrawer,
      organizationName,
      currentUser,
      userName,
      userInitials,
      reachesMultipleFacilities,
      buildVersion,
      isDark,
      toggleDarkMode,
      toolbarIconColor,
    };
  },
  methods: {
    doLogout() {
      // do not await for apollo cache to be cleared to avoid updating components without authentication
      this.logout(this.$apollo);
      this.$router.replace({ name: "Login" });
    },
  },
};
</script>

<style scoped>
/* Subtle hairline separates the header at rest (elevate-on-scroll adds a shadow
   on scroll). Theme-aware so it reads on the dark navy app-bar too. Vuetify 2
   exposes no prop for an app-bar bottom border, so this stays as CSS. */
.v-app-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.09) !important;
}
.theme--dark .v-app-bar {
  border-bottom-color: rgba(255, 255, 255, 0.12) !important;
}

@media print {
  .v-app-bar {
    box-shadow: none !important;
  }
}
</style>
